import * as Sentry from "@sentry/vue";
import { showChunkLoadFailure } from "../chunkLoadFailure";
import { claimForReport, describeChunkError, errorMessage, extractChunkUrl, isChunkLoadError, type ChunkErrorInfo } from "./chunkErrors";

// The SDK is bundled, so these never touch an undeclared global. Before init (and in dev, where it is
// disabled) the calls are no-ops.

export function captureMessage(message: string) {
	Sentry.captureMessage(message);
	if (import.meta.env.DEV) console.log("Sentry message:", message);
}

export function addBreadcrumb(message: string, data?: Record<string, unknown>) {
	Sentry.addBreadcrumb({
		category: "map",
		level: "info",
		message,
		data,
	});
	if (import.meta.env.DEV) console.log("Sentry breadcrumb:", message, data);
}

export function captureException(error: Error) {
	if (isChunkLoadError(error)) {
		showChunkLoadFailure();
		if (claimForReport(error)) reportChunkError(error, describeChunkError(error, { source: "captureException" }));
	} else {
		Sentry.captureException(error);
	}
	if (import.meta.env.DEV) console.error("Sentry exception:", error);
}

export function setUser(user: { uid: string; email?: string | null } | null | undefined) {
	if (!user) {
		Sentry.setUser(null);
		return;
	}
	Sentry.setUser({ id: user.uid, email: user.email ?? undefined });
}

const TAG_MAX = 200;

function tag(value: string | undefined): string | undefined {
	return value ? value.slice(0, TAG_MAX) : undefined;
}

export function reportChunkError(error: unknown, info: ChunkErrorInfo) {
	const exception = error instanceof Error ? error : new Error(info.message);
	Sentry.captureException(exception, {
		mechanism: { type: `auto.chunk_error.${info.source}`, handled: true },
		captureContext: {
			level: "error",
			tags: {
				chunk_error: info.chunkError ? "true" : "unclassified",
				chunk_error_source: info.source,
				chunk_url: tag(info.chunkUrl),
				target_route: tag(info.targetRoute ?? info.targetPath),
			},
			contexts: {
				chunk_error: {
					source: info.source,
					chunk_url: info.chunkUrl,
					target_route: info.targetRoute,
					target_path: info.targetPath,
					from_path: info.fromPath,
					page_url: info.pageUrl,
					message: info.message,
				},
			},
		},
	});
	if (import.meta.env.DEV) console.error("Sentry chunk error:", info);
}

/**
 * `beforeSend` hook. Chunk-load errors that reach Sentry through the SDK's global handlers (an unhandled
 * rejection from a bare import() call) get the same tag as the ones reported above.
 */
export function tagChunkErrorEvent(event: Sentry.ErrorEvent, hint: Sentry.EventHint): Sentry.ErrorEvent | null {
	if (event.tags?.chunk_error) return event;
	const message = hint.originalException !== undefined
		? errorMessage(hint.originalException)
		: event.exception?.values?.[0]?.value ?? "";
	if (!isChunkLoadError(message)) return event;
	// The same failure can arrive here after the router or preload handler already reported it (an
	// unhandled router.push() rejection, for one). Report each failure once.
	if (hint.originalException && !claimForReport(hint.originalException)) return null;
	event.tags = {
		...event.tags,
		chunk_error: "true",
		chunk_error_source: "global_handler",
		chunk_url: tag(extractChunkUrl(message)),
	};
	return event;
}
